import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

type ScaleValue = 1 | 2 | 3 | 4 | 5;
type Relevance = 'niedrig' | 'mittel' | 'hoch';
type FieldAccent = 'mensch' | 'navy' | 'nachhaltigkeit' | 'resilienz';

type FieldItem = {
  id: string;
  title: string;
  text: string;
};

type TransformationField = {
  id: string;
  number: number;
  title: string;
  goal: string;
  accent: FieldAccent;
  items: FieldItem[];
};

const COMPANY_SIZES = [
  { value: 'bis-10', label: 'bis 10' },
  { value: '11-50', label: '11 bis 50' },
  { value: '51-100', label: '51 bis 100' },
  { value: '101-250', label: '101 bis 250' },
  { value: 'mehr-als-250', label: 'mehr als 250 Mitarbeitende' },
] as const;

const INDUSTRIES = [
  { value: 'industrie', label: 'Industrie / Verarbeitendes Gewerbe' },
  { value: 'handwerk', label: 'Handwerk' },
  { value: 'dienstleistung', label: 'Dienstleistung' },
  { value: 'sonstige', label: 'Sonstige' },
] as const;

const SCALE_OPTIONS: { value: ScaleValue; label: string }[] = [
  { value: 1, label: 'stimme gar nicht zu' },
  { value: 2, label: 'stimme eher nicht zu' },
  { value: 3, label: 'teils/teils' },
  { value: 4, label: 'stimme eher zu' },
  { value: 5, label: 'stimme voll zu' },
];

const RELEVANCE_OPTIONS: { value: Relevance; label: string }[] = [
  { value: 'niedrig', label: 'Niedrig' },
  { value: 'mittel', label: 'Mittel' },
  { value: 'hoch', label: 'Hoch' },
];

const FIELDS: TransformationField[] = [
  {
    id: 'mensch',
    number: 1,
    title: 'Mensch, Kultur & Führung',
    goal: 'Den Menschen als aktiven Gestalter und Treiber der Transformation verankern und bedarfsgerecht befähigen.',
    accent: 'mensch',
    items: [
      {
        id: '1.1',
        title: 'Führung & Strategie',
        text: 'Unsere Unternehmensführung verfolgt eine klare Strategie für die digitale und nachhaltige Zukunft unseres Betriebs.',
      },
      {
        id: '1.2',
        title: 'Partizipation & Mitgestaltung',
        text: 'Wir binden Beschäftigte frühzeitig in Veränderungsprozesse ein und fördern eine aktive Mitgestaltungskultur (z. B. durch ein digitales Ideenmanagement oder bereichsübergreifende Projektteams).',
      },
      {
        id: '1.3',
        title: 'Vorausschauende Qualifizierung',
        text: 'Wir bilden unsere Belegschaft gezielt und frühzeitig für zukünftige Aufgaben weiter – bevor Engpässe oder Überlastungen im Betrieb entstehen.',
      },
      {
        id: '1.4',
        title: 'Wissenssicherung & Einarbeitung',
        text: 'Wir nutzen digitale Hilfsmittel (z. B. kurze Videos, Sprachnotizen, Wissens-Apps), um das Erfahrungswissen unserer Mitarbeitenden im Betrieb zu halten und neue Kolleg:innen schneller einzuarbeiten.',
      },
    ],
  },
  {
    id: 'assistenz',
    number: 2,
    title: 'Unterstützende Werkzeuge & Assistenzsysteme',
    goal: 'Neue Technologien gezielt einsetzen, um Beschäftigte spürbar zu entlasten, Arbeitsabläufe zu vereinfachen und Prozesse einfach beherrschbar zu halten.',
    accent: 'navy',
    items: [
      {
        id: '2.1',
        title: 'Physisch entlastende Assistenzsysteme',
        text: 'Wir nutzen technische Hilfsmittel (z. B. kollaborative Roboter, Hebehilfen oder automatische Transportfahrzeuge), um Beschäftigte bei körperlich schwerer oder monotoner Arbeit zu entlasten.',
      },
      {
        id: '2.2',
        title: 'Digitale Unterstützung am Arbeitsplatz',
        text: 'Unsere Mitarbeitenden können benötigte Informationen, Skizzen und Schritt-für-Schritt-Anleitungen direkt am Arbeitsplatz digital abrufen (z. B. über Tablets oder Monitore).',
      },
      {
        id: '2.3',
        title: 'KI-Unterstützung im Alltag',
        text: 'Wir setzen KI-Technologien ein, um Daten gezielt auszuwerten oder wiederkehrende Routineaufgaben automatisch zu erledigen.',
      },
    ],
  },
  {
    id: 'kreislaufe',
    number: 3,
    title: 'Grüne Wertschöpfung & Intelligente Kreisläufe',
    goal: 'Nachhaltigkeit und Ressourceneffizienz von der Pflicht zum strategischen Wettbewerbsvorteil entwickeln.',
    accent: 'nachhaltigkeit',
    items: [
      {
        id: '3.1',
        title: 'Nachhaltige Entscheidungskultur',
        text: 'Umweltschutz und Ressourceneffizienz sind bei uns nicht nur eine regulatorische Pflicht, sondern fester Bestandteil bei unseren alltäglichen Entscheidungen und Investitionen.',
      },
      {
        id: '3.2',
        title: 'Energietransparenz & Betriebsdaten',
        text: 'Wir erfassen Verbräuche von Energie, Strom oder Rohstoffen an unseren Anlagen und Arbeitsplätzen kontinuierlich über digitale Messgeräte oder Sensoren.',
      },
      {
        id: '3.3',
        title: 'Digitale Umwelt- & Materialnachweise',
        text: 'Wir führen Material- und CO₂-Nachweise digital, um gesetzliche Vorgaben oder Nachweispflichten gegenüber Kunden mühelos zu erfüllen.',
      },
    ],
  },
  {
    id: 'lieferketten',
    number: 4,
    title: 'Sichere, flexible & vernetzte Lieferketten',
    goal: 'Transparente, störungsrobuste und wandlungsfähige Prozesse sowie geschützte IT-Infrastrukturen aufbauen.',
    accent: 'resilienz',
    items: [
      {
        id: '4.1',
        title: 'Datenbasierte Prozesssteuerung',
        text: 'Wir steuern unsere Kernprozesse primär auf Basis von Daten statt nach Erfahrung und Bauchgefühl.',
      },
      {
        id: '4.2',
        title: 'Digitale Transparenz in der Lieferkette',
        text: 'Wir nutzen digitale Werkzeuge (z. B. Barcodes, QR-Codes oder automatische Sendungsnachverfolgung), um den Standort und Status von Rohstoffen, Bauteilen und Lieferungen jederzeit transparent im Blick zu haben.',
      },
      {
        id: '4.3',
        title: 'Flexibilität bei Störungen',
        text: 'Unsere Prozesse sind so flexibel aufgestellt, dass wir bei Ausfällen von Material, Maschinen oder Personal kurzfristig umsteuern und unsere Lieferfähigkeit sichern können.',
      },
      {
        id: '4.4',
        title: 'Sichere IT-Infrastruktur & Datensouveränität',
        text: 'Wir sichern unsere IT-Systeme, Cloud-Dienste und geschäftskritischen Daten verlässlich gegen Ausfälle, Cyber-Angriffe und Datenabfluss ab.',
      },
      {
        id: '4.5',
        title: 'Kooperation in Netzwerken',
        text: 'Um bei unerwarteten Engpässen oder neuen technischen Hürden handlungsfähig zu bleiben, greifen wir flexibel auf ein externes Partnernetzwerk zurück (z. B. Technologiepartner, Hochschulen oder Branchennetzwerke), statt zeitkritische Lösungen alleine zu entwickeln.',
      },
    ],
  },
  {
    id: 'produkte',
    number: 5,
    title: 'Kundennahe Produkte & Geschäftsmodelle',
    goal: 'Durch digitale Zusatzleistungen und kundennahe Services neue Wettbewerbsvorteile sichern.',
    accent: 'navy',
    items: [
      {
        id: '5.1',
        title: 'Kundennahe Entwicklung',
        text: 'Wir nutzen Rückmeldungen und Nutzungsdaten unserer Kund:innen systematisch, um unsere Angebote und Produkte gezielt zu verbessern.',
      },
      {
        id: '5.2',
        title: 'Digitale Zusatzleistungen',
        text: 'Wir bieten unseren Kund:innen über das reine Produkt hinaus digitale Services an, die ihnen die Nutzung erleichtern oder zusätzlichen Nutzen bringen (z. B. eine App zur Steuerung, automatische Erinnerungen an Service/Wartung oder digitale Nutzungsberichte).',
      },
      {
        id: '5.3',
        title: 'Digitale Kundenschnittstellen',
        text: 'Wir machen es Kund:innen so einfach wie möglich, mit uns in Kontakt zu treten und Aufträge abzuwickeln (z. B. durch Online-Terminbuchung, digitale Produkt-Konfiguratoren, Kundenportale oder Online-Statusabfragen).',
      },
      {
        id: '5.4',
        title: 'Virtuelle Tests & Digitaler Zwilling',
        text: 'Wir nutzen digitale Modelle oder Abbilder (z. B. Digitale Zwillinge), um Abläufe oder Produktvarianten vorab virtuell zu simulieren und schadlos zu testen.',
      },
    ],
  },
];

const ALL_ITEMS = FIELDS.flatMap((field) => field.items);

function emptyItemAnswers(): Record<string, { ist: ScaleValue | null; relevanz: Relevance | null }> {
  return Object.fromEntries(ALL_ITEMS.map((item) => [item.id, { ist: null, relevanz: null }]));
}

function labelFor(options: readonly { value: string; label: string }[], value: string) {
  return options.find((option) => option.value === value)?.label ?? value;
}

function formatScale(value: ScaleValue | null) {
  if (!value) return '–';
  return `${value} (${SCALE_OPTIONS.find((option) => option.value === value)?.label})`;
}

function formatRelevance(value: Relevance | null) {
  if (!value) return '–';
  return labelFor(RELEVANCE_OPTIONS, value);
}

function ScaleGroup({
  name,
  value,
  onChange,
  disabled,
  labelledBy,
}: {
  name: string;
  value: ScaleValue | null;
  onChange: (value: ScaleValue) => void;
  disabled?: boolean;
  labelledBy: string;
}) {
  return (
    <div className="selbstcheck-scale" role="radiogroup" aria-labelledby={labelledBy}>
      {SCALE_OPTIONS.map((option) => {
        const optionId = `${name}-${option.value}`;
        return (
          <label key={option.value} className="selbstcheck-choice" htmlFor={optionId}>
            <input
              id={optionId}
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              required
            />
            <span>
              <strong>{option.value}</strong>
              <em>{option.label}</em>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function RelevanceGroup({
  name,
  value,
  onChange,
  disabled,
  labelledBy,
}: {
  name: string;
  value: Relevance | null;
  onChange: (value: Relevance) => void;
  disabled?: boolean;
  labelledBy: string;
}) {
  return (
    <div className="selbstcheck-relevance" role="radiogroup" aria-labelledby={labelledBy}>
      {RELEVANCE_OPTIONS.map((option) => {
        const optionId = `${name}-${option.value}`;
        return (
          <label key={option.value} className={`selbstcheck-choice selbstcheck-choice--${option.value}`} htmlFor={optionId}>
            <input
              id={optionId}
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              required
            />
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function Selbstcheck() {
  const clientRef = useRef<ReturnType<typeof generateClient<Schema>> | null>(null);
  const getClient = () => {
    if (!clientRef.current) {
      clientRef.current = generateClient<Schema>();
    }
    return clientRef.current;
  };

  const [companySize, setCompanySize] = useState('');
  const [industry, setIndustry] = useState('');
  const [industryOther, setIndustryOther] = useState('');
  const [currentStanding, setCurrentStanding] = useState<ScaleValue | null>(null);
  const [futureOptimism, setFutureOptimism] = useState<ScaleValue | null>(null);
  const [itemAnswers, setItemAnswers] = useState(emptyItemAnswers);
  const [unternehmen, setUnternehmen] = useState('');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [position, setPosition] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [auswertungsgespraech, setAuswertungsgespraech] = useState(true);
  const [projektUpdates, setProjektUpdates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const progress = useMemo(() => {
    let answered = 0;
    let total = 4 + ALL_ITEMS.length * 2 + 4;
    if (companySize) answered += 1;
    if (industry && (industry !== 'sonstige' || industryOther.trim())) answered += 1;
    if (currentStanding) answered += 1;
    if (futureOptimism) answered += 1;
    ALL_ITEMS.forEach((item) => {
      if (itemAnswers[item.id]?.ist) answered += 1;
      if (itemAnswers[item.id]?.relevanz) answered += 1;
    });
    if (unternehmen.trim()) answered += 1;
    if (vorname.trim()) answered += 1;
    if (nachname.trim()) answered += 1;
    if (email.trim()) answered += 1;
    return Math.round((answered / total) * 100);
  }, [
    companySize,
    industry,
    industryOther,
    currentStanding,
    futureOptimism,
    itemAnswers,
    unternehmen,
    vorname,
    nachname,
    email,
  ]);

  const firstMissingId = () => {
    if (!companySize) return 'a1';
    if (!industry) return 'a2';
    if (industry === 'sonstige' && !industryOther.trim()) return 'industryOther';
    if (!currentStanding) return 'a3-current';
    if (!futureOptimism) return 'a3-future';
    for (const item of ALL_ITEMS) {
      if (!itemAnswers[item.id]?.ist) return `item-${item.id}-ist`;
      if (!itemAnswers[item.id]?.relevanz) return `item-${item.id}-relevanz`;
    }
    if (!unternehmen.trim()) return 'unternehmen';
    if (!vorname.trim()) return 'vorname';
    if (!nachname.trim()) return 'nachname';
    if (!email.trim()) return 'email';
    return null;
  };

  const updateItem = (id: string, patch: { ist?: ScaleValue; relevanz?: Relevance }) => {
    setItemAnswers((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const industryLabel =
    industry === 'sonstige' && industryOther.trim()
      ? `Sonstige: ${industryOther.trim()}`
      : labelFor(INDUSTRIES, industry);

  const buildMessage = () => {
    const lines = [
      'ST5-Selbstcheck',
      '',
      '--- Block A: Allgemeine Unternehmensangaben ---',
      `A1 Unternehmensgröße: ${labelFor(COMPANY_SIZES, companySize)}`,
      `A2 Hauptbranche: ${industryLabel}`,
      `A3 Aktuell strukturell und wirtschaftlich gut aufgestellt: ${formatScale(currentStanding)}`,
      `A3 Optimistisch für die Zukunft positioniert: ${formatScale(futureOptimism)}`,
      '',
    ];

    FIELDS.forEach((field) => {
      lines.push(`--- Feld ${field.number}: ${field.title} ---`);
      field.items.forEach((item) => {
        const answer = itemAnswers[item.id];
        lines.push(`${item.id} ${item.title}`);
        lines.push(`  ${item.text}`);
        lines.push(`  Ist-Zustand: ${formatScale(answer.ist)}`);
        lines.push(`  Zukunfts-Relevanz: ${formatRelevance(answer.relevanz)}`);
        lines.push('');
      });
    });

    lines.push('--- Kontakt ---');
    lines.push(`Unternehmen: ${unternehmen.trim()}`);
    lines.push(`Name: ${vorname.trim()} ${nachname.trim()}`);
    lines.push(`Position: ${position.trim() || '–'}`);
    lines.push(`E-Mail: ${email.trim()}`);
    lines.push(`Telefon: ${telefon.trim() || '–'}`);
    lines.push(`Auswertungsgespräch gewünscht: ${auswertungsgespraech ? 'Ja' : 'Nein'}`);
    lines.push(`Projekt-Updates gewünscht: ${projektUpdates ? 'Ja' : 'Nein'}`);
    return lines.join('\n');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setSubmitError(null);

    const missingId = firstMissingId();
    if (missingId) {
      setFormError('Bitte beantworten Sie alle Pflichtangaben, bevor Sie den Selbstcheck absenden.');
      document.getElementById(missingId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setLoading(true);
    const message = buildMessage();

    try {
      await getClient().models.ContactSubmission.create(
        {
          unternehmenInstitution: unternehmen.trim(),
          vorname: vorname.trim(),
          nachname: nachname.trim(),
          position: position.trim() || undefined,
          email: email.trim(),
          telefon: telefon.trim() || undefined,
          message,
          interesseAustausch: auswertungsgespraech,
          projektUpdates,
        },
        { authMode: 'identityPool' }
      );
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      setSubmitError(errorMessage);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const results = useMemo(() => {
    return FIELDS.map((field) => {
      const rows = field.items.map((item) => {
        const answer = itemAnswers[item.id];
        const ist = answer.ist ?? 1;
        const relevanz = answer.relevanz ?? 'niedrig';
        const isStrength = ist >= 4;
        const isDevelopment = relevanz === 'hoch' && ist <= 3;
        return { item, ist, relevanz, isStrength, isDevelopment };
      });
      const average = rows.reduce((sum, row) => sum + row.ist, 0) / rows.length;
      return { field, rows, average };
    });
  }, [itemAnswers]);

  if (submitted) {
    const strengths = results.flatMap((group) => group.rows.filter((row) => row.isStrength));
    const developments = results.flatMap((group) => group.rows.filter((row) => row.isDevelopment));

    return (
      <div className="selbstcheck-page">
        <section className="selbstcheck-hero">
          <div className="section-inner">
            <p className="selbstcheck-kicker">Selbstcheck</p>
            <h1>Vielen Dank für Ihre Zeit!</h1>
            <p className="selbstcheck-lead">
              Mit Ihrer Einschätzung haben Sie den Grundstein für Ihre Standortbestimmung gelegt.
              Hier ist Ihr Stärken- und Entwicklungsprofil – Ist-Stand im Verhältnis zur
              Zukunfts-Relevanz.
            </p>
          </div>
        </section>

        <section className="selbstcheck-content">
          <div className="section-inner">
            {submitError && (
              <div className="selbstcheck-error" role="alert">
                Die Übermittlung an das ST5-Team ist fehlgeschlagen ({submitError}). Ihr Profil
                bleibt hier sichtbar. Bitte senden Sie uns den Bogen zusätzlich an{' '}
                <a href="mailto:info@st-5.eu">info@st-5.eu</a>.
              </div>
            )}

            {!submitError && (
              <div className="selbstcheck-success" role="status">
                Ihre Angaben wurden übermittelt. Unsere Transfer-Scouts können die Ergebnisse
                gemeinsam mit Ihnen einordnen und eine individuelle ST5-Transformations-Roadmap
                ableiten.
              </div>
            )}

            <article className="selbstcheck-card">
              <h2>Ihr Profil auf einen Blick</h2>
              <p>
                {unternehmen.trim() || 'Ihr Unternehmen'} · {industryLabel} ·{' '}
                {labelFor(COMPANY_SIZES, companySize)}
              </p>
              <div className="selbstcheck-overview-grid">
                {results.map((group) => (
                  <div
                    key={group.field.id}
                    className={`selbstcheck-overview-item selbstcheck-accent--${group.field.accent}`}
                  >
                    <span>Feld {group.field.number}</span>
                    <strong>{group.field.title}</strong>
                    <div className="selbstcheck-meter" aria-hidden="true">
                      <i style={{ width: `${(group.average / 5) * 100}%` }} />
                    </div>
                    <small>Ist-Stand im Mittel: {group.average.toFixed(1)} von 5</small>
                  </div>
                ))}
              </div>
            </article>

            {developments.length > 0 && (
              <article className="selbstcheck-card">
                <h2>Wirksamste Hebel der nächsten 2–3 Jahre</h2>
                <p>
                  Hohe Zukunfts-Relevanz bei noch ausbaufähigem Ist-Stand – hier liegt ungehobenes
                  Potenzial.
                </p>
                <ul className="selbstcheck-result-list">
                  {developments.map((row) => (
                    <li key={row.item.id}>
                      <strong>
                        {row.item.id} {row.item.title}
                      </strong>
                      <span>
                        Ist {row.ist}/5 · Relevanz {formatRelevance(row.relevanz)}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            )}

            {strengths.length > 0 && (
              <article className="selbstcheck-card">
                <h2>Ihre Stärken</h2>
                <p>Aussagen, die bereits gut auf Ihr Unternehmen zutreffen.</p>
                <ul className="selbstcheck-result-list">
                  {strengths.map((row) => (
                    <li key={row.item.id}>
                      <strong>
                        {row.item.id} {row.item.title}
                      </strong>
                      <span>
                        Ist {row.ist}/5 · Relevanz {formatRelevance(row.relevanz)}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            )}

            {results.map((group) => (
              <article
                key={group.field.id}
                className={`selbstcheck-card selbstcheck-accent--${group.field.accent}`}
              >
                <p className="selbstcheck-field-kicker">Feld {String(group.field.number).padStart(2, '0')}</p>
                <h2>{group.field.title}</h2>
                <div className="selbstcheck-result-rows">
                  {group.rows.map((row) => (
                    <div key={row.item.id} className="selbstcheck-result-row">
                      <div>
                        <strong>
                          {row.item.id} {row.item.title}
                        </strong>
                        <p>{row.item.text}</p>
                      </div>
                      <div className="selbstcheck-result-scores">
                        <span>Ist {row.ist}/5</span>
                        <span>Relevanz {formatRelevance(row.relevanz)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}

            <article className="selbstcheck-card selbstcheck-card--cta">
              <h2>Die nächsten Schritte</h2>
              <p>
                Gerne ordnen wir die Daten in einem kostenlosen 30-minütigen Auswertungsgespräch
                ein und leiten daraus Ihre individuelle ST5-Transformations-Roadmap sowie passende
                Folgeformate ab.
              </p>
              <div className="selbstcheck-cta-row">
                <Link to="/kontakt" className="selbstcheck-submit">
                  Auswertungsgespräch anfragen
                </Link>
                <a href="mailto:info@st-5.eu">info@st-5.eu</a>
                <a href="tel:+4915170650315">0151 70650315</a>
              </div>
            </article>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="selbstcheck-page">
      <section className="selbstcheck-hero">
        <div className="section-inner">
          <p className="selbstcheck-kicker">Selbstcheck</p>
          <h1>Der ST5-Selbstcheck für den Mittelstand</h1>
          <p className="selbstcheck-lead">
            Standorte sichern, Prozesse stärken, Zukunft gestalten.
          </p>
        </div>
      </section>

      <section className="selbstcheck-content">
        <div className="section-inner">
          <article className="selbstcheck-card">
            <h2>Willkommen beim ST5-Selbstcheck!</h2>
            <p>
              Wo steht Ihr Unternehmen bei entscheidenden Zukunftsfragen – und wo liegen die
              wirksamsten Hebel für die nächsten Jahre? Dieser Selbstcheck bietet Ihnen eine
              strukturierte, praxistaugliche Standortbestimmung für Ihren Unternehmensalltag.
            </p>
            <p>
              In knapp zehn Minuten durchleuchten Sie fünf zentrale Transformationsfelder: von der
              Unternehmenskultur über unterstützende Assistenzsysteme und nachhaltige Prozesse bis
              hin zu vernetzten, sicheren Wertschöpfungsketten und smarten Produkten, die
              Kund:innen zusätzlichen Mehrwert liefern.
            </p>
          </article>

          <div className="selbstcheck-benefits">
            <article className="selbstcheck-card">
              <h3>Klarheit</h3>
              <p>
                Sie erhalten ein übersichtliches Stärken- und Entwicklungsprofil Ihres Unternehmens
                (Ist-Stand vs. Zukunfts-Relevanz).
              </p>
            </article>
            <article className="selbstcheck-card">
              <h3>Flexible Nutzung</h3>
              <p>
                Der Check funktioniert als eigenständige Orientierung für Ihre interne Planung
                genauso wie als optimale Grundlage für ein gemeinsames vertiefendes Erstgespräch
                mit unseren ST5-Transfer-Scouts.
              </p>
            </article>
            <article className="selbstcheck-card">
              <h3>Konkrete Perspektiven</h3>
              <p>
                Gerne ordnen wir im Rahmen eines Erstgesprächs die Daten gemeinsam mit Ihnen ein
                und leiten daraus Ihre individuelle ST5-Transformations-Roadmap sowie passende
                Folgeformate ab.
              </p>
            </article>
          </div>

          <aside className="selbstcheck-note">
            <strong>Hinweis für Dienstleistungs- &amp; Handwerksbetriebe</strong>
            <p>
              Der ST5-Selbstcheck richtet sich an alle Branchen. Wenn in den Fragen Begriffe wie
              „Produktion“, „Material“ oder „Anlagen“ auftauchen, übersetzen Sie diese einfach
              gedanklich auf Ihre Kernleistung (z. B. Produktion = Leistungserbringung /
              Projektabwicklung, Material / Anlagen = Arbeitsmittel, Daten &amp; IT-Systeme).
            </p>
          </aside>

          <div className="selbstcheck-progress" aria-live="polite">
            <span>Fortschritt</span>
            <div className="selbstcheck-progress-bar" aria-hidden="true">
              <i style={{ width: `${progress}%` }} />
            </div>
            <strong>{progress}&nbsp;%</strong>
          </div>

          <form className="selbstcheck-form" onSubmit={handleSubmit} noValidate>
            {formError && (
              <div className="selbstcheck-error" role="alert">
                {formError}
              </div>
            )}

            <fieldset className="selbstcheck-card" id="a1">
              <legend>Block A: Allgemeine Unternehmensangaben</legend>

              <div className="selbstcheck-question">
                <h3>A1. Unternehmensgröße</h3>
                <div className="selbstcheck-options" role="radiogroup" aria-label="Unternehmensgröße">
                  {COMPANY_SIZES.map((option) => (
                    <label key={option.value} className="selbstcheck-choice">
                      <input
                        type="radio"
                        name="companySize"
                        value={option.value}
                        checked={companySize === option.value}
                        onChange={() => setCompanySize(option.value)}
                        disabled={loading}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="selbstcheck-question" id="a2">
                <h3>A2. Hauptbranche</h3>
                <div className="selbstcheck-options" role="radiogroup" aria-label="Hauptbranche">
                  {INDUSTRIES.map((option) => (
                    <label key={option.value} className="selbstcheck-choice">
                      <input
                        type="radio"
                        name="industry"
                        value={option.value}
                        checked={industry === option.value}
                        onChange={() => setIndustry(option.value)}
                        disabled={loading}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                {industry === 'sonstige' && (
                  <label className="selbstcheck-text-label" htmlFor="industryOther">
                    Bitte nennen Sie Ihre Branche
                    <input
                      id="industryOther"
                      type="text"
                      value={industryOther}
                      onChange={(event) => setIndustryOther(event.target.value)}
                      disabled={loading}
                      required
                    />
                  </label>
                )}
              </div>

              <div className="selbstcheck-question">
                <h3>A3. Erfolgs- &amp; Zukunftseinschätzung</h3>
                <p className="selbstcheck-hint">
                  Skala 1 = stimme gar nicht zu bis 5 = stimme voll zu
                </p>
                <div id="a3-current">
                  <p className="selbstcheck-statement" id="current-standing-label">
                    Unser Unternehmen steht aktuell strukturell und wirtschaftlich gut da.
                  </p>
                  <ScaleGroup
                    name="currentStanding"
                    value={currentStanding}
                    onChange={setCurrentStanding}
                    disabled={loading}
                    labelledBy="current-standing-label"
                  />
                </div>
                <div id="a3-future">
                  <p className="selbstcheck-statement" id="future-optimism-label">
                    Ich bin optimistisch, dass unser Unternehmen für die Zukunft gut positioniert
                    ist.
                  </p>
                  <ScaleGroup
                    name="futureOptimism"
                    value={futureOptimism}
                    onChange={setFutureOptimism}
                    disabled={loading}
                    labelledBy="future-optimism-label"
                  />
                </div>
              </div>
            </fieldset>

            <section className="selbstcheck-card">
              <h2>Block B: Die fünf Transformationsfelder</h2>
              <p>
                Für jede der folgenden Kernaussagen bewerten Sie bitte zwei Dimensionen:
              </p>
              <ul className="selbstcheck-legend">
                <li>
                  <strong>Ist-Zustand (1 bis 5):</strong> Wie genau trifft die Aussage aktuell auf
                  Ihr Unternehmen zu?
                </li>
                <li>
                  <strong>Zukunfts-Relevanz (Niedrig / Mittel / Hoch):</strong> Wie wichtig ist
                  dieses Thema für die Wettbewerbsfähigkeit Ihres Unternehmens in den nächsten 2–3
                  Jahren? Sollten einzelne Werkzeuge für Ihr Geschäftsmodell grundsätzlich
                  unpassend sein, bewerten Sie die Zukunfts-Relevanz mit Niedrig und den Ist-Stand
                  mit 1.
                </li>
              </ul>
              <div className="selbstcheck-tip">
                <strong>Tipp zur Priorisierung („Unbekanntes Potenzial“)</strong>
                <p>
                  Fragen Sie sich bei der Zukunfts-Relevanz weniger: „Kennen wir das schon?“,
                  sondern vielmehr:
                </p>
                <ul>
                  <li>
                    <strong>Druck:</strong> Entsteht hier spürbarer Druck durch Kund:innen,
                    Gesetzgebung oder Fachkräftemangel?
                  </li>
                  <li>
                    <strong>Chancen:</strong> Könnte uns in diesem Bereich eine konkrete
                    technologische, organisatorische oder prozessuale Maßnahme helfen, ein
                    ungelöstes Problem zu beheben (z. B. Ausschuss senken, Stress reduzieren,
                    Einarbeitung verkürzen)?
                  </li>
                  <li>
                    <strong>Unruhe / Unwissen:</strong> Wenn Sie merken „Da wissen wir eigentlich
                    noch zu wenig drüber“, stufen Sie die Relevanz bewusst als Hoch ein. Das zeigt
                    Ihnen, wo bisher ungehobenes Potenzial schlummert.
                  </li>
                </ul>
              </div>
            </section>

            {FIELDS.map((field) => (
              <fieldset
                key={field.id}
                className={`selbstcheck-card selbstcheck-accent--${field.accent}`}
              >
                <legend>
                  Feld {field.number}: {field.title}
                </legend>
                <p className="selbstcheck-goal">
                  <strong>Ziel:</strong> {field.goal}
                </p>

                {field.items.map((item) => (
                  <div key={item.id} className="selbstcheck-question" id={`item-${item.id}`}>
                    <h3>
                      {item.id} {item.title}
                    </h3>
                    <p className="selbstcheck-statement">{item.text}</p>

                    <div id={`item-${item.id}-ist`}>
                      <p className="selbstcheck-dimension" id={`${item.id}-ist-label`}>
                        Ist-Zustand
                      </p>
                      <ScaleGroup
                        name={`ist-${item.id}`}
                        value={itemAnswers[item.id]?.ist ?? null}
                        onChange={(value) => updateItem(item.id, { ist: value })}
                        disabled={loading}
                        labelledBy={`${item.id}-ist-label`}
                      />
                    </div>

                    <div id={`item-${item.id}-relevanz`}>
                      <p className="selbstcheck-dimension" id={`${item.id}-relevanz-label`}>
                        Zukunfts-Relevanz
                      </p>
                      <RelevanceGroup
                        name={`relevanz-${item.id}`}
                        value={itemAnswers[item.id]?.relevanz ?? null}
                        onChange={(value) => updateItem(item.id, { relevanz: value })}
                        disabled={loading}
                        labelledBy={`${item.id}-relevanz-label`}
                      />
                    </div>
                  </div>
                ))}
              </fieldset>
            ))}

            <fieldset className="selbstcheck-card">
              <legend>Ihre Kontaktdaten</legend>
              <p>
                Damit wir Ihnen die Ergebnisse zuordnen und bei Bedarf ein kostenloses
                30-minütiges Auswertungsgespräch vereinbaren können.
              </p>

              <div className="selbstcheck-contact-grid">
                <label htmlFor="unternehmen">
                  Unternehmen / Institution <span className="required">*</span>
                  <input
                    id="unternehmen"
                    type="text"
                    value={unternehmen}
                    onChange={(event) => setUnternehmen(event.target.value)}
                    disabled={loading}
                    required
                  />
                </label>
                <label htmlFor="position">
                  Position
                  <input
                    id="position"
                    type="text"
                    value={position}
                    onChange={(event) => setPosition(event.target.value)}
                    disabled={loading}
                  />
                </label>
                <label htmlFor="vorname">
                  Vorname <span className="required">*</span>
                  <input
                    id="vorname"
                    type="text"
                    value={vorname}
                    onChange={(event) => setVorname(event.target.value)}
                    disabled={loading}
                    required
                  />
                </label>
                <label htmlFor="nachname">
                  Nachname <span className="required">*</span>
                  <input
                    id="nachname"
                    type="text"
                    value={nachname}
                    onChange={(event) => setNachname(event.target.value)}
                    disabled={loading}
                    required
                  />
                </label>
                <label htmlFor="email">
                  E-Mail <span className="required">*</span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={loading}
                    required
                  />
                </label>
                <label htmlFor="telefon">
                  Telefon
                  <input
                    id="telefon"
                    type="tel"
                    value={telefon}
                    onChange={(event) => setTelefon(event.target.value)}
                    disabled={loading}
                  />
                </label>
              </div>

              <label className="selbstcheck-check">
                <input
                  type="checkbox"
                  checked={auswertungsgespraech}
                  onChange={(event) => setAuswertungsgespraech(event.target.checked)}
                  disabled={loading}
                />
                Ich möchte ein kostenloses 30-minütiges Auswertungsgespräch mit den
                ST5-Transfer-Scouts.
              </label>
              <label className="selbstcheck-check">
                <input
                  type="checkbox"
                  checked={projektUpdates}
                  onChange={(event) => setProjektUpdates(event.target.checked)}
                  disabled={loading}
                />
                Ich möchte über das Projekt und Beteiligungsmöglichkeiten auf dem Laufenden
                gehalten werden.
              </label>
            </fieldset>

            <div className="selbstcheck-submit-row">
              <button type="submit" className="selbstcheck-submit" disabled={loading}>
                {loading ? 'Wird gesendet…' : 'Selbstcheck absenden'}
              </button>
              <p>
                Mit dem Absenden willigen Sie in die Verarbeitung Ihrer Angaben zur Auswertung und
                Kontaktaufnahme ein. Details finden Sie in der{' '}
                <Link to="/datenschutz">Datenschutzerklärung</Link>.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

export default Selbstcheck;
